import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TButtonData, TViewCommonOption, TWidget } from '@libs/types';
import React, { ElementType, MouseEvent, MouseEventHandler } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import { getIconHierarchy } from './ButtonCircleView';

type TButtonRowStyleProps = Pick<TButtonOption, 'margin' | 'iconName' | 'iconVariant' | 'size' | 'alignment' | 'hierarchy' | 'iconHierarchy'>;

export type TButtonRowViewProps = {
    widget?: TWidget<TButtonData, TButtonOption>;
    uid?: string;
    label?: string;
    as?: ElementType;
    /**
     * Called when the button is clicked. Optional in the standalone (non-widget) form
     * when the element is used as a non-interactive label (e.g. `as="div"`).
     */
    onClick?: MouseEventHandler;
    children?: React.ReactNode;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames'> &
    TButtonRowStyleProps;

type THierarchy = NonNullable<TButtonOption['hierarchy']>;

const HIERARCHY_COLOR_MAP: Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'>> = {
    highlighted: {
        main: semanticColors[SemanticColor.TextEmphasised],
        hovered: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    primary: {
        main: semanticColors[SemanticColor.TextEmphasised],
        hovered: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    destructive: {
        main: semanticColors[SemanticColor.TextError],
        hovered: {
            main: semanticColors[SemanticColor.TextError],
        },
        pressed: {
            main: semanticColors[SemanticColor.TextError],
        },
        disabled: {
            main: semanticColors[SemanticColor.TextErrorDisabled],
        },
    },
    secondary: {
        main: semanticColors[SemanticColor.TextEmphasised],
        hovered: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    tertiary: {
        main: semanticColors[SemanticColor.TextEmphasised],
        hovered: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: semanticColors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    tertiaryGlass: {},
};

const HIERARCHY_BG_MAP: Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'>> = {
    highlighted: {
        main: semanticColors[SemanticColor.FillSecondary],
        hovered: {
            main: semanticColors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillSecondaryPressed],
        },
        disabled: {
            main: semanticColors[SemanticColor.FillSecondaryDisabled],
        },
    },
    primary: {
        main: semanticColors[SemanticColor.FillSecondary],
        hovered: {
            main: semanticColors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillSecondaryPressed],
        },
        disabled: {
            main: semanticColors[SemanticColor.FillSecondaryDisabled],
        },
    },
    destructive: {
        main: semanticColors[SemanticColor.FillDestructive],
        hovered: {
            main: semanticColors[SemanticColor.FillDestructiveHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillDestructivePressed],
        },
        disabled: {
            main: semanticColors[SemanticColor.FillDestructiveDisabled],
        },
    },
    secondary: {
        main: semanticColors[SemanticColor.FillSecondary],
        hovered: {
            main: semanticColors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillSecondaryPressed],
        },
        disabled: {
            main: semanticColors[SemanticColor.FillSecondaryDisabled],
        },
    },
    tertiary: {
        main: semanticColors[SemanticColor.FillSecondary],
        hovered: {
            main: semanticColors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillSecondaryPressed],
        },
        disabled: {
            main: semanticColors[SemanticColor.FillSecondaryDisabled],
        },
    },
    tertiaryGlass: {},
};

const SIZE_PADDING_MAP: Record<string, Pick<TCoreButtonStyleProps, 'padding'>> = {
    large: {
        padding: { top: 24, right: 16, bottom: 24, left: 24 },
    },
    largeWithIcon: {
        padding: { top: 16, right: 24, bottom: 16, left: 16 },
    },
    small: {
        padding: { top: 16, right: 16, bottom: 16, left: 16 },
    },
    smallWithIcon: {
        padding: { top: 8, right: 24, bottom: 8, left: 16 },
    },
};

/**
 * Composable building block for the ButtonRow widget.
 * Accepts pre-resolved behavior props (isDisabled, isHide, onClick) from the parent,
 * making it suitable for scenarios where behavior state is already managed one level up.
 * Delegates theme resolution and all rendering to CoreButtonView.
 */
export const ButtonRowView = ({
    uid,
    widget,
    label,
    isDisabled,
    isHide,
    onClick,
    as = 'button',
    parentStyleNames,
    iconName,
    iconVariant = 'simple',
    size,
    alignment,
    hierarchy = 'primary' as THierarchy,
    iconHierarchy,
    margin,
}: TButtonRowViewProps) => {
    const resolvedParentStyleNames = parentStyleNames ?? [];

    const hasSizeKey = size === 'large' ? (iconName ? 'largeWithIcon' : 'large') : iconName ? 'smallWithIcon' : 'small';

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        ...HIERARCHY_BG_MAP[hierarchy],
        ...SIZE_PADDING_MAP[hasSizeKey],
        fillContainer: true,
        margin: margin ?? {},
        borderRadius: 0,
        fontStyle: { fontSize: 17, lineHeight: 24 },
        cursorDisabled: 'default',
        textAlign: (['center', 'start', 'end'] as const).find((v) => v === alignment) ?? 'start',
        gap: alignment === 'center' ? 8 : 16,
    };

    return (
        <CoreButtonView
            uid={uid}
            widget={widget as TCoreButtonViewProps['widget']}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={coreButtonStyleProps}
            parentStyleNames={resolvedParentStyleNames}
            as={as}
            styleName="rowButton"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onClick?.(e);
            }}
        >
            <ConditionalRender condition={!!iconName}>
                <VisualView
                    parentStyleNames={resolvedParentStyleNames}
                    styleName="rowButtonIcon"
                    isDisabled={isDisabled}
                    iconName={iconName}
                    iconVariant={iconVariant}
                    iconHierarchy={iconHierarchy ? getIconHierarchy(iconHierarchy as string) : hierarchy === 'destructive' ? 'destructive' : 'emphasised'}
                />
            </ConditionalRender>
            <CoreTextView
                text={label}
                isDisabled={isDisabled}
                as="span"
                parentStyleNames={resolvedParentStyleNames}
                styleName="rowButtonText"
                styleProps={{
                    ...TextStyle.main,
                    ...HIERARCHY_COLOR_MAP[hierarchy],
                }}
            />
        </CoreButtonView>
    );
};
